import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    ManualEntryTemplateDetails,
    ManualEntryTemplatePermissions,
    ManualEntryTemplateSettings,
    ManualEntryTemplateActivities
} from 'pages/employee-settings/manual-entry-templates/shared';
import { UserAccess } from 'components/user-access';
import { Breadcrumbs } from 'components/navigation';
import { PageRestricted } from 'pages/errors/page-restricted';
import { Wizard } from 'components/wizard';
import { AlertConfirm, AlertUnsavedData, NotificationToaster } from 'components/notifications';
import { ManualEntryService } from 'services';
import { Intent } from '@blueprintjs/core';
import { useNavigate } from 'react-router-dom';
import { useDirtyData } from 'hooks/useDirtyData';
import { useDebounce } from 'hooks/useDebounce';
import { HelperFunctions } from 'helpers';

export function ManualEntryTemplateCreate({ breadcrumbs = [] }) {

    const manualEntryMultiType = "MANUALENTRYMULTI";
    const manualEntrySingleType = "MANUALENTRYSINGLE";
    const stepActivitiesId = 1;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);

    const originalTemplateModel = {
        ruleset: '',
        access: []
    }
    
    const debounceTimeout = 1000;

    const [manualEntryOptions, setManualEntryOptions] = useState({});
    const [template, setTemplate] = useState({});
    const [originalTemplate, setOriginalTemplate] = useState(originalTemplateModel);
    const [templateType, setTemplateType] = useState("");
    const [settingsValid, setSettingsValid] = useState(false);
    const [permissionsValid, setPermissionsValid] = useState(false);
    const [activitiesValid, setActivitiesValid] = useState(false);
    const [detailsValid, setDetailsValid] = useState(false);
    const [infringements, setInfringements] = useState([]);
    const [showInfringementModal, setShowInfringementModal] = useState(false);
    const infringementsConfirmed = useRef(false);

    const requiredAction = "Employees:ManualEntryManage";
    var navigate = useNavigate();

    const isDirty = useDirtyData(originalTemplate, template);
    const [created, setCreated] = useState(false);
    
    
    const debouncedActivity = useDebounce(template?.activities, debounceTimeout);
    const debouncedMultiActivity = useDebounce(template?.days, debounceTimeout);
    const currentTemplateType = useRef(null);
    const currentRuleset = useRef(null);
    const currentActivity = useRef(null);
    const currentMultiActivity = useRef(null);
    const currentIsValid = useRef(false);
    const wizardControls = useRef([]);

    const init = () => {
        ManualEntryService.getOptions().then(function (optionsResponse) {
            setManualEntryOptions(optionsResponse);
            setLoading(false);
        }, function () {
            NotificationToaster.show("danger", "Could not load settings, please refresh the page");
        });
    };

    useEffect(init, []);

    function saveTemplate() {
        setSaving(true);

        ManualEntryService.createTemplate(templateType, template).then(function () {
            setTemplate(originalTemplateModel);
            setOriginalTemplate(originalTemplateModel);
            setTemplateType("");
            NotificationToaster.show(Intent.SUCCESS, "Your template was created successfully");
            setCreated(true);
        }, function (error) {

            const validationError = 400;
            if(error.response && error.response.status === validationError) {
                NotificationToaster.show(Intent.WARNING, error.response.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "There was an error creating your template, please try again");
            }

        }).finally(function () {
            setSaving(false);
        })

    }

    useEffect(() => {
        if (!isDirty && created) {
            navigate("/employee-settings/manual-entry-templates");
        }
    }, [isDirty, created])

    function onTemplateTypeChange(type) {
        setTemplateType(type);
    }

    function onTemplateUpdate(updates) {

        setTemplate((prev) => {
            return {
                ...prev,
                ...updates
            };
        })
    }

    function onBeforeStepChange(step, onNextStep, onPrevStep) {

        wizardControls.current = [onPrevStep, onNextStep];

        return new Promise((resolve, reject) => {
            if (step === 2 && (templateType === manualEntrySingleType || templateType === manualEntryMultiType) && !infringementsConfirmed.current) {

                checkForInfringements().then((response) => {
                    if (response?.length > 0) {
                        setShowInfringementModal(true);
                        reject();
                    } else {
                        resolve();
                    }
                });
            } else {
                resolve();
            }
        })
    }

    function onWizardChange(step) {
        setWizardStep(step);
    }

    const isValid = useCallback(() => {
        const stepSettingsId = 0;
        const stepPermissionsId = 2;
        const stepDetailsId = 3;

        if (wizardStep === stepSettingsId) {
            return settingsValid;
        }

        if (wizardStep === stepActivitiesId) {
            return activitiesValid;
        }

        if (wizardStep === stepPermissionsId) {
            return permissionsValid;
        }

        if (wizardStep === stepDetailsId) {
            return detailsValid;
        }
        
        return false;
    }, [wizardStep, settingsValid, activitiesValid, permissionsValid, detailsValid]);

    useEffect(() => {
        if (wizardStep !== stepActivitiesId) {
            return;
        }
        
        if (
            currentTemplateType.current === templateType &&
            currentRuleset.current === template?.ruleset &&
            currentIsValid.current === isValid() &&
            HelperFunctions.deepEqual(currentActivity.current, template?.activities, { strict: true }) &&
            HelperFunctions.deepEqual(currentMultiActivity.current, template?.days, { strict: true })
        ) {
            return;
        }

        if (currentTemplateType.current !== templateType) {
            setInfringements([]);
        }

        if (currentTemplateType.current !== templateType || currentRuleset.current !== template?.ruleset) {
            setInfringements([]);
        }

        currentTemplateType.current = templateType;
        currentRuleset.current = template?.ruleset;
        currentActivity.current = template?.activities ? [...template.activities.map(item => ({ ...item }))] : undefined;
        currentMultiActivity.current = copyMultiDays(template?.days);
        currentIsValid.current = isValid();
        
        if (templateType === manualEntrySingleType && (template?.activities == null || template?.activities?.length === 0)) {
            // Do not reset while editing
            return;
        }
        
        if (templateType === manualEntryMultiType && (template?.days == null || template?.days?.length === 0)) {
            // Do not reset while editing
            return;
        }
        
        checkForInfringements().catch(() => {});
    }, [templateType, debouncedActivity, debouncedMultiActivity, template.ruleset, wizardStep, isValid]);

    function checkForInfringements() {
        if ((templateType !== manualEntrySingleType && templateType !== manualEntryMultiType) || !isValid()) {
            setInfringements([]);
            return Promise.resolve([]);
        }

        const requestDays = templateType === manualEntryMultiType ? template?.days : [{
            dayNumber: 1,
            shifts: [
                [
                    ...template?.activities ?? []
                ]
            ]
        }];

        const request = {
            ruleset: template?.ruleset,
            Days: requestDays
        };

        return new Promise((resolve, reject) => {

            ManualEntryService.checkForInfringments(request).then((response) => {
                setInfringements(response);
                resolve(response);
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not check for infringements, please try again later");
                reject();
            });

        });

    }

    function copyShifts(shifts) {
        if (!Array.isArray(shifts)) return [];
        return shifts.map(shift => Array.isArray(shift) ? shift.map(item => ({ ...item })) : []);
    }

    function copyMultiDays(days) {
        if (!Array.isArray(days)) return undefined;
        return days.map(day => day ? { ...day, shifts: copyShifts(day.shifts) } : null);
    }

    function onInfringementModalCancel() {
        setShowInfringementModal(false);
    }

    function onInfringementModalConfirm() {
        setShowInfringementModal(false);
        infringementsConfirmed.current = true;

        if (wizardControls.current != null && wizardControls.current.length === 2) {
            wizardControls.current[1]();
        }
    }


    return (
        <UserAccess perform={requiredAction}
            yes={() => (

                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <h1>New Manual Entry Template</h1>
                    <div>
                        <Wizard onStepChange={onWizardChange} onFinish={saveTemplate} canProceed={isValid()} disabled={saving} loading={loading} saveBetweenSteps saveAction={onBeforeStepChange} showSaveButton={false}>

                            <div>
                                <h2>Template Settings</h2>
                                <ManualEntryTemplateSettings
                                    template={template}
                                    options={manualEntryOptions}
                                    templateType={templateType}
                                    loading={loading}
                                    saving={saving}
                                    onValidChange={setSettingsValid}
                                    onTemplateTypeChange={onTemplateTypeChange}
                                    onTemplateUpdate={onTemplateUpdate}
                                />
                            </div>
                            <div>
                                <ManualEntryTemplateActivities
                                    templateType={templateType}
                                    template={template}
                                    infringements={infringements}
                                    onActivitiesValidChange={setActivitiesValid}
                                    onTemplateUpdate={onTemplateUpdate}
                                />
                            </div>
                            <div>
                                <h2>Permissions</h2>
                                <ManualEntryTemplatePermissions
                                    template={template}
                                    options={manualEntryOptions}
                                    loading={loading}
                                    saving={saving}
                                    onTemplateUpdate={onTemplateUpdate}
                                    onValidChange={setPermissionsValid}
                                />
                            </div>
                            <div>
                                <h2>Template Details</h2>
                                <ManualEntryTemplateDetails
                                    template={template}
                                    loading={loading}
                                    saving={saving}
                                    onTemplateUpdate={onTemplateUpdate}
                                    onValidChange={setDetailsValid}
                                />
                            </div>

                        </Wizard>
                        <AlertUnsavedData
                            isDirty={(isDirty || templateType.length > 0) && !created}>
                        </AlertUnsavedData>

                        <AlertConfirm
                            isOpen={showInfringementModal}
                            title='Template Contains Infringements'
                            confirmButtonIntent='primary'
                            onCancel={onInfringementModalCancel}
                            onConfirm={onInfringementModalConfirm}
                        >
                            <p>Are you sure you want to continue, if you apply this template to employees they will receive infringements as a result.</p>
                        </AlertConfirm>

                    </div>
                </div>

            )}
            no={() => (
                <PageRestricted />
            )}
        />);
}